import { Accordion, AccordionDetails, Grid, SelectChangeEvent, Typography } from "@mui/material";
import AccordionSummarySlim from "components/Common/AccordionSummarySlim";
import AutocompleteForm from "components/Common/AutocompleteForm";
import SelectForm from "components/Common/SelectForm";
import TextFieldForm from "components/Common/TextFieldForm";
import Constants from "helpers/constants";
import usePricingMethods from "helpers/context/SelectionValues/usePricingMethods";
import useSalespersons from "helpers/context/SelectionValues/useSalespersons";
import useTerms from "helpers/context/SelectionValues/useTerms";
import { PricingMethodEnum } from "helpers/enums";
import useLocaleNumberFormatter from "helpers/hooks/useLocaleNumberFormatter";
import { useTranslations } from "@fenetech/translations";
import React, { useCallback, useMemo, useState } from 'react';
import CustomerPricingCategories from "./CustomerPricingCategories";
import TaxFieldForm from "./TaxFieldForm";
import { useController, useFieldArray, useFormContext } from "react-hook-form";
import { IEmployee } from "models/IEmployee";
import useApplicationInfo from "helpers/context/Application/useApplicationInfo";

interface IProps {
    expanded: boolean,
    onExpandedChange: (event: React.SyntheticEvent, expanded: boolean) => void,
    handleSalesPersonChange: (salesPersonID: number) => Promise<void>,
    readOnly: boolean,
    parentCustomerID: string,
    parentSiteID: string | undefined,
    pricingMethod: PricingMethodEnum
}

const CustomerFinancial: React.FC<IProps> = (props: IProps) => {

    const tm = useTranslations();
    const lnf = useLocaleNumberFormatter({ style: "decimal", useGrouping: true, minimumFractionDigits: 4, maximumFractionDigits: 4 });
    const pricingMethods = usePricingMethods();
    const salespersons = useSalespersons(props.parentCustomerID, props.parentSiteID ?? props.parentCustomerID);
    const terms = useTerms(props.parentCustomerID);
    const applicationInfo = useApplicationInfo();
    
    const isPricingMethodPercentBased = useCallback((pricingMethod: PricingMethodEnum) => {
        switch (pricingMethod)
        {
            case PricingMethodEnum.Discount:
                return !applicationInfo.parameters.pricingShowDiscountMultiplierInsteadOfPercent;
            case PricingMethodEnum.Markup:
                return !applicationInfo.parameters.pricingShowMarkupMultiplierInsteadOfPercent;
            case PricingMethodEnum.Markdown:
                return true;
            case PricingMethodEnum.Margin:
                return true;
            default:
                return true;
        }
    }, [applicationInfo]);

    const currentPricingMethodIsPercentBased = useMemo(() => {
        return isPricingMethodPercentBased(props.pricingMethod);
    }, [isPricingMethodPercentBased, props.pricingMethod])

    const { control, getValues, setValue } = useFormContext();
    const { field } = useController({
        name: "customer.salesPersonID", 
        control,
        rules: { required: false },
    });

    const { fields: pricingFields } = useFieldArray({
        control: control,
        name: "pricing"
    });

    const [originalSalesPerson] = useState(field.value);

    const filteredSalesPersonList = useMemo(() => {
        const inactiveTranslation = "<" + tm.Get("Inactive") + ">";
        if (salespersons === undefined) {
            return undefined;
        }
        let filteredList: IEmployee[] = [];
        salespersons.forEach(sp => {
            if (!sp.inactive || sp.employeeID === originalSalesPerson) {
                filteredList.push({employeeID: sp.employeeID, inactive: sp.inactive, fullName: (sp.inactive ? inactiveTranslation : "") + sp.fullName});
            }
        });
        return filteredList;
    }, [originalSalesPerson, salespersons, tm]);

    const getDefaultPricingValue = React.useCallback((pricingMethod: PricingMethodEnum) => {
        let defaultValue: number
        const percentBased: boolean = isPricingMethodPercentBased(pricingMethod);
        switch (pricingMethod) {
            case PricingMethodEnum.Discount:
                defaultValue = percentBased ? Constants.Default.DiscountPercent : Constants.Default.DiscountMultiplier;
                break; 
            case PricingMethodEnum.Margin:
                defaultValue = Constants.Default.MarginPercent;
                break; 
            default:
                defaultValue = percentBased ? Constants.Default.MarkupPercent : Constants.Default.MarkupMultiplier;
                break;
        }
        return lnf.Format(defaultValue);
    }, [isPricingMethodPercentBased, lnf]);

    const onPricingMethodChange = useCallback((e: SelectChangeEvent<any>, value: any) => {
        const prevValues = getValues("pricing");
        pricingFields.forEach((field, index) => {
            setValue(`pricing.${index}`, { ...prevValues[index], discountPercent: getDefaultPricingValue(e.target.value), discountTableID: -1 })
        });
    }, [getValues, setValue, pricingFields, getDefaultPricingValue]);

    return <>
        <Accordion expanded={props.expanded} onChange={props.onExpandedChange} disableGutters >
            <AccordionSummarySlim>
                <Typography>
                    {tm.Get("Financial")}
                </Typography>
            </AccordionSummarySlim>
            <AccordionDetails>
                <Grid container spacing={2} padding={0}>
                    <Grid item xs={12}>
                        <SelectForm
                            fieldName="customer.pricingMethod"
                            dataSource={pricingMethods}
                            keyField="id"
                            valueField="id"
                            displayField="description"
                            translateDisplayField={true}
                            label={tm.Get("Pricing Method")}
                            readOnly={props.readOnly}
                            onChange={onPricingMethodChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomerPricingCategories
                            pricingMethod={props.pricingMethod}
                            isPricingMethodPercentBased={currentPricingMethodIsPercentBased}
                            readOnly={props.readOnly}
                            parentCustomerID={props.parentCustomerID}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <AutocompleteForm
                            fieldName="customer.terms"
                            dataSource={terms}
                            valueField="description"
                            defaultValue=""
                            displayField="description"
                            translateDisplayField={false}
                            label={tm.Get("Terms")}
                            readOnly={props.readOnly}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <AutocompleteForm
                            fieldName="customer.salesPersonID"
                            dataSource={filteredSalesPersonList}
                            valueField="employeeID"
                            defaultValue={0}
                            displayField="fullName"
                            translateDisplayField={false}
                            label={tm.Get("Salesperson")}
                            readOnly={props.readOnly}
                            onChangeCallback={props.handleSalesPersonChange}
                        />
                    </Grid>
                    {[...Array(5)].map((e, i) => {
                        return <React.Fragment key={i}>
                            <Grid item xs={12} sm={6}>
                                <TaxFieldForm
                                    fieldName={`customer.taxPercent${i > 0 ? i.toString() : ""}`}
                                    readOnly={props.readOnly}
                                    lnf={lnf}
                                    label={tm.Get(`Tax ${i > 0 ? i.toString() : ""}`.trim())}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldForm
                                    fieldName={`customer.taxTitle${i > 0 ? i.toString() : ""}`}
                                    readOnly={props.readOnly}
                                    variant="standard"
                                    id={`customer.taxTitle${i > 0 ? i.toString() : ""}`}
                                    inputProps={{ maxLength: Constants.MaxLength.CustomerTaxTitles }}
                                    label={i > 0 ? tm.Get(`Tax Title ${i.toString()}`) : tm.Get("Tax Title")}
                                    placeholder={i > 0 ? tm.Get(`Tax Title ${i.toString()}`) : tm.Get("Tax Title")}
                                    fullWidth
                                />
                            </Grid>
                        </React.Fragment>
                    })}
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>;
}

export default CustomerFinancial;